import React from 'react';
import { useEffect, useState } from "react";
import AppConst from "../apis/appconst";
import axios from "axios";
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function StockReports() {
    const [formData, setFormData] = useState({
        stockList: [],
        search_key: '',
        from_date: new Date().toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0],
        statusFilter: 'all' // 'all', 'active', 'expired'
    });

    const [filteredData, setFilteredData] = useState([]);
    
    // ✅ NEW: Expiring medicines state
    const [expiringMedicines, setExpiringMedicines] = useState([]);
    const [showExpiryAlert, setShowExpiryAlert] = useState(false);
    const [isLoadingExpiry, setIsLoadingExpiry] = useState(false);

    const initData = async () => {
        const storedData = sessionStorage.getItem("ldata");

        if (storedData) {
            const fd = new FormData();
            fd.append('from_date', formData.from_date);
            fd.append('to_date', formData.to_date);

            try {
                const response = await axios.post(
                    AppConst.BASE_URL + AppConst.getStockReportUrl, 
                    fd, 
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );
                
                if (response.data.status) {
                    const records = response.data.records;
                    if (records != null && records !== undefined) {
                        setFormData(prev => ({
                            ...prev,
                            stockList: records
                        }));
                        setFilteredData(records);
                    }
                } else {
                    toast.error(response.data.message || "Failed to fetch stock data");
                }
            } catch (error) {
                console.error(error);
                toast.error("Error fetching stock reports");
            }
        }
    };

    // ✅ NEW: Fetch expiring medicines
    const fetchExpiringMedicines = async () => {
        setIsLoadingExpiry(true);
        try {
            const response = await axios.post(
                AppConst.BASE_URL + AppConst.getExpiringMedicinesUrl,
                {},
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.data.status) {
                const medicines = response.data.records || [];
                setExpiringMedicines(medicines);
                setShowExpiryAlert(true);
                
                if (medicines.length === 0) {
                    toast.info("No medicines expiring within 30 days");
                }
            } else {
                toast.error(response.data.message || "Failed to fetch expiring medicines");
            }
        } catch (error) {
            console.error("Error fetching expiring medicines:", error);
            toast.error("Error fetching expiring medicines");
        } finally {
            setIsLoadingExpiry(false);
        }
    };

    // ✅ NEW: Export expiring medicines to PDF
    const exportExpiringMedicinesToPDF = () => {
        if (expiringMedicines.length === 0) {
            toast.warning("No expiring medicines to export");
            return;
        }

        try {
            const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            
            // Title
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.text('Medicines Expiring Within 30 Days', pageWidth / 2, 15, { align: 'center' });
            
            // Date
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            const today = new Date().toLocaleDateString('en-GB');
            doc.text(`Report Generated: ${today}`, pageWidth / 2, 22, { align: 'center' });
            
            // Summary
            const criticalCount = expiringMedicines.filter(m => m.days_remaining <= 7).length;
            const warningCount = expiringMedicines.filter(m => m.days_remaining > 7 && m.days_remaining <= 15).length;
            const noticeCount = expiringMedicines.filter(m => m.days_remaining > 15).length;
            
            doc.setFontSize(9);
            doc.setTextColor(220, 53, 69); // Red
            doc.text(`Critical (≤7 days): ${criticalCount}`, 14, 30);
            
            doc.setTextColor(255, 193, 7); // Yellow
            doc.text(`Warning (8-15 days): ${warningCount}`, 70, 30);
            
            doc.setTextColor(13, 110, 253); // Blue
            doc.text(`Notice (16-30 days): ${noticeCount}`, 140, 30);
            
            doc.setTextColor(0, 0, 0); // Reset to black
            doc.text(`Total: ${expiringMedicines.length}`, 210, 30);
            
            // Table
            const tableHeaders = [
                'Sl#', 
                'Medicine Name', 
                'Supplier', 
                'Batch No', 
                'Package Qty', 
                'Loose Qty', 
                'Expiry Date', 
                'Days Left',
                'Status'
            ];
            
            const tableBody = expiringMedicines.map((medicine, index) => {
                let status = '';
                if (medicine.days_remaining <= 7) {
                    status = 'Critical';
                } else if (medicine.days_remaining <= 15) {
                    status = 'Warning';
                } else {
                    status = 'Notice';
                }
                
                return [
                    index + 1,
                    medicine.medicine_name || '-',
                    medicine.supplier_name || '-',
                    medicine.batch_no || '-',
                    medicine.package_qty || 0,
                    medicine.loose_qty || 0,
                    medicine.expiry_date || '-',
                    `${medicine.days_remaining} day${medicine.days_remaining !== 1 ? 's' : ''}`,
                    status
                ];
            });
            
            autoTable(doc, {
                startY: 35,
                head: [tableHeaders],
                body: tableBody,
                theme: 'grid',
                styles: { 
                    fontSize: 8,
                    cellPadding: 2,
                    overflow: 'linebreak',
                    halign: 'left'
                },
                headStyles: { 
                    fillColor: [64, 81, 137],
                    textColor: 255,
                    fontStyle: 'bold',
                    halign: 'center'
                },
                columnStyles: {
                    0: { halign: 'center', cellWidth: 10 },
                    1: { cellWidth: 55 },
                    2: { cellWidth: 45 },
                    3: { cellWidth: 30 },
                    4: { halign: 'center', cellWidth: 20 },
                    5: { halign: 'center', cellWidth: 20 },
                    6: { halign: 'center', cellWidth: 28 },
                    7: { halign: 'center', cellWidth: 22 },
                    8: { halign: 'center', cellWidth: 22 }
                },
                didParseCell: function(data) {
                    // Color code the status column
                    if (data.column.index === 8 && data.section === 'body') {
                        const status = data.cell.raw;
                        if (status === 'Critical') {
                            data.cell.styles.textColor = [220, 53, 69];
                            data.cell.styles.fontStyle = 'bold';
                        } else if (status === 'Warning') {
                            data.cell.styles.textColor = [255, 193, 7];
                            data.cell.styles.fontStyle = 'bold';
                        } else if (status === 'Notice') {
                            data.cell.styles.textColor = [13, 110, 253];
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                    
                    // Color code days left
                    if (data.column.index === 7 && data.section === 'body') {
                        const rowIndex = data.row.index;
                        const daysRemaining = expiringMedicines[rowIndex]?.days_remaining || 0;
                        
                        if (daysRemaining <= 7) {
                            data.cell.styles.textColor = [220, 53, 69];
                            data.cell.styles.fontStyle = 'bold';
                        } else if (daysRemaining <= 15) {
                            data.cell.styles.textColor = [255, 193, 7];
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                },
                margin: { top: 35, right: 10, bottom: 20, left: 10 },
                didDrawPage: function(data) {
                    // Footer
                    doc.setFontSize(8);
                    doc.setTextColor(128);
                    doc.text(
                        `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
                        pageWidth / 2,
                        pageHeight - 10,
                        { align: 'center' }
                    );
                }
            });
            
            // Save PDF
            const fileName = `Expiring_Medicines_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(fileName);
            
            toast.success("PDF exported successfully!");
        } catch (error) {
            console.error('PDF Export error:', error);
            toast.error("Failed to export PDF");
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleClearFilters = () => {
        const today = new Date().toISOString().split('T')[0];
        setFormData(prev => ({
            ...prev,
            search_key: "",
            from_date: today,
            to_date: today,
            statusFilter: 'all'
        }));
    };

    // Handle status filter change
    const handleStatusFilter = (status) => {
        setFormData(prev => ({
            ...prev,
            statusFilter: status
        }));
    };

    // Filter data based on search and status
    useEffect(() => {
        let filtered = formData.stockList;
        
        // Apply search filter
        if (formData.search_key.trim() !== '') {
            const searchLower = formData.search_key.toLowerCase();
            filtered = filtered.filter(item => 
                item.purchase_supplier?.toLowerCase().includes(searchLower) ||
                item.purchase_item_medicine?.toLowerCase().includes(searchLower) ||
                item.purchase_item_batch?.toLowerCase().includes(searchLower)
            );
        }
        
        // Apply status filter
        if (formData.statusFilter === 'active') {
            filtered = filtered.filter(item => 
                !isExpired(item.purchase_item_expiry_month, item.purchase_item_expiry_year)
            );
        } else if (formData.statusFilter === 'expired') {
            filtered = filtered.filter(item => 
                isExpired(item.purchase_item_expiry_month, item.purchase_item_expiry_year)
            );
        }
        
        setFilteredData(filtered);
    }, [formData.search_key, formData.stockList, formData.statusFilter]);

    useEffect(() => {
        initData();
    }, []);

    // Auto-reload when dates change
    useEffect(() => {
        if (formData.from_date && formData.to_date) {
            initData();
        }
    }, [formData.from_date, formData.to_date]);

    // Check if medicine is expired
    const isExpired = (month, year) => {
        if (!month || !year) return false;
        
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = currentDate.getFullYear();
        
        let expiryMonth = parseInt(month);
        let expiryYear = parseInt(year);
        
        if (expiryYear < 100) {
            expiryYear = 2000 + expiryYear;
        }
        
        if (expiryYear < currentYear) {
            return true;
        } else if (expiryYear === currentYear && expiryMonth < currentMonth) {
            return true;
        }
        return false;
    };

    // Format month number to name
    const getMonthName = (monthNum) => {
        if (!monthNum) return '-';
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                           'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const index = parseInt(monthNum) - 1;
        return monthNames[index] || '-';
    };

    // Format expiry date
    const formatExpiryDate = (month, year) => {
        if (!month || !year) return '-';
        
        let fullYear = parseInt(year);
        
        if (fullYear < 100) {
            fullYear = 2000 + fullYear;
        }
        
        return `${getMonthName(month)}-${fullYear}`;
    };

    // Export to CSV
    const exportToCSV = () => {
        try {
            const headers = ['Sl No', 'Supplier', 'Medicine', 'Package Qty', 'Loose Qty', 'Batch No', 'Expiry Date', 'MRP', 'Status'];
            
            const rows = filteredData.map((item, index) => [
                index + 1,
                `"${(item.purchase_supplier || '-').replace(/"/g, '""')}"`,
                `"${(item.purchase_item_medicine || '-').replace(/"/g, '""')}"`,
                item.purchase_item_qty_stock || '0',
                item.purchase_item_tab_cap_stock || '0',
                `"${(item.purchase_item_batch || '-').replace(/"/g, '""')}"`,
                formatExpiryDate(item.purchase_item_expiry_month, item.purchase_item_expiry_year),
                parseFloat(item.purchase_item_pur_price || 0).toFixed(2),
                isExpired(item.purchase_item_expiry_month, item.purchase_item_expiry_year) ? 'Expired' : 'Not Expired'
            ]);
            
            const csvContent = [
                headers.join(','),
                ...rows.map(row => row.join(','))
            ].join('\n');
            
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            
            link.setAttribute('href', url);
            link.setAttribute('download', `Stock_Report_${formData.from_date}_to_${formData.to_date}.csv`);
            link.style.visibility = 'hidden';
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            toast.success("CSV exported successfully!");
        } catch (error) {
            console.error('Export error:', error);
            toast.error("Failed to export CSV");
        }
    };

    // Print function
    const handlePrint = () => {
        window.print();
    };

    // DataTable Columns
    const columns = [
        {
            name: 'Sl No',
            selector: (row, index) => index + 1,
            sortable: false,
            width: '80px',
            center: true
        },
        {
            name: 'Supplier Name',
            selector: row => row.purchase_supplier,
            sortable: true,
            grow: 2,
            wrap: true
        },
        {
            name: 'Medicine Name',
            selector: row => row.purchase_item_medicine,
            sortable: true,
            grow: 2,
            wrap: true
        },
        {
            name: 'Package Qty',
            selector: row => row.purchase_item_qty_stock || 0,
            sortable: true,
            width: '130px',
            center: true,
            cell: row => (
                <span className="badge bg-primary-subtle text-primary fs-6">
                    {row.purchase_item_qty_stock || 0}
                </span>
            )
        },
        {
            name: 'Loose Qty',
            selector: row => row.purchase_item_tab_cap_stock || 0,
            sortable: true,
            width: '120px',
            center: true,
            cell: row => (
                <span className="badge bg-info-subtle text-info fs-6">
                    {row.purchase_item_tab_cap_stock || 0}
                </span>
            )
        },
        {
            name: 'Batch No',
            selector: row => row.purchase_item_batch,
            sortable: true,
            width: '130px'
        },
        {
            name: 'Expiry Date',
            selector: row => formatExpiryDate(row.purchase_item_expiry_month, row.purchase_item_expiry_year),
            sortable: true,
            width: '130px',
            cell: row => (
                <span>
                    {formatExpiryDate(row.purchase_item_expiry_month, row.purchase_item_expiry_year)}
                </span>
            )
        },
        {
            name: 'MRP',
            selector: row => row.purchase_item_pur_price,
            sortable: true,
            width: '120px',
            right: true,
            cell: row => `₹${parseFloat(row.purchase_item_pur_price || 0).toFixed(2)}`
        },
        {
            name: 'Status',
            width: '130px',
            center: true,
            cell: row => {
                const expired = isExpired(row.purchase_item_expiry_month, row.purchase_item_expiry_year);
                
                return (
                    <span className={`badge ${expired ? 'bg-danger' : 'bg-success'}`}>
                        {expired ? 'Expired' : 'Not Expired'}
                    </span>
                );
            }
        }
    ];

    // Custom styles for DataTable
    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#405189',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 'bold',
                borderRadius: '5px 5px 0 0'
            }
        },
        headCells: {
            style: {
                color: '#fff',
                paddingLeft: '16px',
                paddingRight: '16px'
            }
        },
        rows: {
            style: {
                fontSize: '14px',
                '&:hover': {
                    backgroundColor: '#f8f9fa',
                }
            }
        }
    };

    return (
        <div className="page-content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="card">
                            <div className="card-header border-0">
                                <div className="row align-items-center gy-3">
                                    <div className="col-sm">
                                        <h5 className="card-title mb-0">
                                            <i className="ri-file-list-3-line me-2"></i>
                                            Stock Reports
                                        </h5>
                                    </div>
                                    <div className="col-sm-auto">
                                        <div className="d-flex gap-2 flex-wrap">
                                            {/* ✅ NEW: Expiring Medicines Button */}
                                            <button 
                                                type="button" 
                                                className="btn btn-warning"
                                                onClick={fetchExpiringMedicines}
                                                disabled={isLoadingExpiry}
                                            >
                                                <i className={`${isLoadingExpiry ? 'ri-loader-4-line rotating' : 'ri-alarm-warning-line'} align-bottom me-1`}></i> 
                                                {isLoadingExpiry ? 'Loading...' : 'Expiring Medicines'}
                                                {expiringMedicines.length > 0 && !isLoadingExpiry && (
                                                    <span className="badge bg-danger ms-2">{expiringMedicines.length}</span>
                                                )}
                                            </button>
                                            
                                            <button 
                                                type="button" 
                                                className="btn btn-success"
                                                onClick={exportToCSV}
                                                disabled={filteredData.length === 0}
                                            >
                                                <i className="ri-file-excel-line align-bottom me-1"></i> 
                                                Export Excel
                                            </button>
                                            <button 
                                                type="button" 
                                                className="btn btn-info no-print"
                                                onClick={handlePrint}
                                                disabled={filteredData.length === 0}
                                            >
                                                <i className="ri-printer-line align-bottom me-1"></i> 
                                                Print
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="card-body border border-dashed border-end-0 border-start-0 no-print">
                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <label className="form-label">From Date</label>
                                        <input 
                                            className="form-control" 
                                            type="date" 
                                            name="from_date"
                                            onChange={handleChange} 
                                            value={formData.from_date} 
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">To Date</label>
                                        <input 
                                            className="form-control" 
                                            type="date" 
                                            name="to_date"
                                            onChange={handleChange} 
                                            value={formData.to_date} 
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label">&nbsp;</label>
                                        <div>
                                            <button 
                                                type="button" 
                                                onClick={handleClearFilters} 
                                                className="btn btn-info w-100"
                                            >
                                                <i className="ri-refresh-line me-1"></i> Clear
                                            </button>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Search</label>
                                        <input 
                                            className="form-control" 
                                            placeholder="Search by Supplier, Medicine or Batch..." 
                                            type="text"
                                            name="search_key" 
                                            onChange={handleChange} 
                                            value={formData.search_key} 
                                        />
                                    </div>
                                </div>
                                
                                {/* Status Filter Buttons */}
                                <div className="row mt-3">
                                    <div className="col-12">
                                        <label className="form-label">Filter by Status:</label>
                                        <div className="btn-group w-100" role="group">
                                            <button
                                                type="button"
                                                className={`btn ${formData.statusFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => handleStatusFilter('all')}
                                            >
                                                <i className="ri-list-check me-1"></i>
                                                All Products
                                                <span className="badge bg-light text-dark ms-2">
                                                    {formData.stockList.length}
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn ${formData.statusFilter === 'active' ? 'btn-success' : 'btn-outline-success'}`}
                                                onClick={() => handleStatusFilter('active')}
                                            >
                                                <i className="ri-checkbox-circle-line me-1"></i>
                                                Active Products
                                                <span className="badge bg-light text-dark ms-2">
                                                    {formData.stockList.filter(item => 
                                                        !isExpired(item.purchase_item_expiry_month, item.purchase_item_expiry_year)
                                                    ).length}
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn ${formData.statusFilter === 'expired' ? 'btn-danger' : 'btn-outline-danger'}`}
                                                onClick={() => handleStatusFilter('expired')}
                                            >
                                                <i className="ri-close-circle-line me-1"></i>
                                                Expired Products
                                                <span className="badge bg-light text-dark ms-2">
                                                    {formData.stockList.filter(item => 
                                                        isExpired(item.purchase_item_expiry_month, item.purchase_item_expiry_year)
                                                    ).length}
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card-body pt-0">
                                <DataTable
                                    columns={columns}
                                    data={filteredData}
                                    customStyles={customStyles}
                                    pagination
                                    paginationPerPage={25}
                                    paginationRowsPerPageOptions={[10, 25, 50, 100, 200]}
                                    highlightOnHover
                                    responsive
                                    striped
                                    dense
                                    noDataComponent={
                                        <div className="text-center py-5">
                                            <i className="ri-inbox-line fs-1 text-muted"></i>
                                            <p className="mt-3">No stock records found</p>
                                        </div>
                                    }
                                />
                                
                                <div className="mt-3 p-3 bg-light rounded">
                                    <div className="row text-center">
                                        <div className="col-md-4">
                                            <strong>Total Records:</strong>
                                            <div className="fs-5 text-primary">{filteredData.length}</div>
                                        </div>
                                        <div className="col-md-4">
                                            <strong>Expired Items:</strong>
                                            <div className="fs-5 text-danger">
                                                {filteredData.filter(item => 
                                                    isExpired(item.purchase_item_expiry_month, item.purchase_item_expiry_year)
                                                ).length}
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <strong>Active Items:</strong>
                                            <div className="fs-5 text-success">
                                                {filteredData.filter(item => 
                                                    !isExpired(item.purchase_item_expiry_month, item.purchase_item_expiry_year)
                                                ).length}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* ✅ NEW: EXPIRING MEDICINES MODAL */}
            {showExpiryAlert && (
                <>
                    <div className="modal fade show" 
                         style={{ display: 'block' }}
                         tabIndex="-1">
                        <div className="modal-dialog modal-xl modal-dialog-scrollable">
                            <div className="modal-content">
                                <div className="modal-header bg-warning text-dark">
                                    <h5 className="modal-title">
                                        <i className="ri-alarm-warning-line me-2"></i>
                                        Medicines Expiring Within 30 Days ({expiringMedicines.length})
                                    </h5>
                                    <button 
                                        type="button" 
                                        className="btn-close" 
                                        onClick={() => setShowExpiryAlert(false)}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    {expiringMedicines.length > 0 ? (
                                        <>
                                            {/* Summary Cards */}
                                            <div className="row mb-4">
                                                <div className="col-md-4">
                                                    <div className="card border-danger">
                                                        <div className="card-body text-center">
                                                            <h6 className="text-danger">
                                                                <i className="ri-error-warning-line"></i> Critical (≤7 days)
                                                            </h6>
                                                            <h3 className="text-danger mb-0">
                                                                {expiringMedicines.filter(m => m.days_remaining <= 7).length}
                                                            </h3>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="card border-warning">
                                                        <div className="card-body text-center">
                                                            <h6 className="text-warning">
                                                                <i className="ri-alert-line"></i> Warning (8-15 days)
                                                            </h6>
                                                            <h3 className="text-warning mb-0">
                                                                {expiringMedicines.filter(m => m.days_remaining > 7 && m.days_remaining <= 15).length}
                                                            </h3>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="card border-info">
                                                        <div className="card-body text-center">
                                                            <h6 className="text-info">
                                                                <i className="ri-information-line"></i> Notice (16-30 days)
                                                            </h6>
                                                            <h3 className="text-info mb-0">
                                                                {expiringMedicines.filter(m => m.days_remaining > 15).length}
                                                            </h3>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Table */}
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-hover">
                                                    <thead className="table-warning">
                                                        <tr>
                                                            <th>Sl. No.</th>
                                                            <th>Medicine Name</th>
                                                            <th>Supplier Name</th>
                                                            <th>Batch No</th>
                                                            <th>Package Qty</th>
                                                            <th>Loose Qty</th>
                                                            <th>Expiry Date</th>
                                                            <th>Days Remaining</th>
                                                            <th>Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {expiringMedicines.map((medicine, index) => {
                                                            let statusClass = '';
                                                            let statusText = '';
                                                            let badgeClass = '';
                                                            
                                                            if (medicine.days_remaining <= 7) {
                                                                statusClass = 'table-danger';
                                                                statusText = 'Critical';
                                                                badgeClass = 'badge bg-danger';
                                                            } else if (medicine.days_remaining <= 15) {
                                                                statusClass = 'table-warning';
                                                                statusText = 'Warning';
                                                                badgeClass = 'badge bg-warning text-dark';
                                                            } else {
                                                                statusClass = 'table-info';
                                                                statusText = 'Notice';
                                                                badgeClass = 'badge bg-info text-dark';
                                                            }
                                                            
                                                            return (
                                                                <tr key={index} className={statusClass}>
                                                                    <td>{index + 1}</td>
                                                                    <td><strong>{medicine.medicine_name}</strong></td>
                                                                    <td>{medicine.supplier_name}</td>
                                                                    <td>
                                                                        <span className="badge bg-secondary">
                                                                            {medicine.batch_no}
                                                                        </span>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <span className="badge bg-primary">
                                                                            {medicine.package_qty}
                                                                        </span>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <span className="badge bg-success">
                                                                            {medicine.loose_qty}
                                                                        </span>
                                                                    </td>
                                                                    <td>{medicine.expiry_date}</td>
                                                                    <td className="text-center">
                                                                        <strong>
                                                                            {medicine.days_remaining} day{medicine.days_remaining !== 1 ? 's' : ''}
                                                                        </strong>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <span className={badgeClass}>
                                                                            {statusText}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center py-5">
                                            <i className="ri-check-line text-success" style={{ fontSize: '48px' }}></i>
                                            <p className="mt-3">No medicines expiring within 30 days</p>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={() => setShowExpiryAlert(false)}
                                    >
                                        Close
                                    </button>
                                    
                                    {/* ✅ PDF Export Button */}
                                    {expiringMedicines.length > 0 && (
                                        <button 
                                            type="button" 
                                            className="btn btn-danger"
                                            onClick={exportExpiringMedicinesToPDF}
                                        >
                                            <i className="ri-file-pdf-line me-1"></i> Export PDF
                                        </button>
                                    )}
                                    
                                    <button 
                                        type="button" 
                                        className="btn btn-primary"
                                        onClick={() => {
                                            fetchExpiringMedicines();
                                            toast.success("Expiry data refreshed");
                                        }}
                                    >
                                        <i className="ri-refresh-line me-1"></i> Refresh
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </>
            )}
            
            {/* Print Styles */}
            <style>{`
                @media print {
                    .no-print {
                        display: none !important;
                    }
                    .card {
                        box-shadow: none !important;
                        border: 1px solid #ddd !important;
                    }
                    body {
                        print-color-adjust: exact;
                        -webkit-print-color-adjust: exact;
                    }
                }
                
                .rotating {
                    animation: rotate 1s linear infinite;
                }
                
                @keyframes rotate {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }
            `}</style>
        </div>
    );
}

export default StockReports;